import { LiaDataElement } from '@liasoft/lit-ui';
import { html, nothing } from 'lit';
import { ago } from '../../shared/ui/format.js';
import { errorPanel, loadingPanel } from '../../shared/ui/templates.js';
import { breadcrumb } from '../../shared/ui/breadcrumb.js';
import { getDeveloper, overview, revokeSession } from '../api/developer.js';
import { PROFILE_PATH } from '../accounts.js';

/**
 * Where the person is signed in: every live client session, revocable one at a time.
 *
 * Including the borrowed one an operator is using, which is the point of showing them at all - an
 * impersonation the person cannot see is the version of that feature that must never ship.
 */
export class UserProfileSessions extends LiaDataElement
{
   static properties = { data: { state: true }, notice: { state: true }, busy: { state: true } };

   constructor()
   {
      super();
      this.data = null;
      this.notice = null;
      this.busy = false;
   }

   async load(signal)
   {
      this.data = await overview(signal);
   }

   /** The session this tab is holding, so it is never offered as something to sign out. */
   get currentPrefix()
   {
      return getDeveloper()?.token?.slice(0, 18) ?? '';
   }

   async revoke(session)
   {
      this.busy = true;
      this.notice = null;

      try
      {
         await revokeSession(session.id);
         this.notice = { variant: 'success', message: `${session.clientName || 'That session'} is signed out.` };
         await this.reload();
      }
      catch (error)
      {
         this.notice = { variant: 'danger', message: error.message };
      }
      finally
      {
         this.busy = false;
      }
   }

   render()
   {
      // Live ones only. A revoked or expired session is a fact about the past, and a list somebody is
      // meant to act on should hold only things they can act on.
      const now = Date.now();
      const rows = (this.data?.sessions ?? [])
         .filter(entry => !entry.revokedAt && (!entry.expiresAt || Date.parse(entry.expiresAt) > now));

      return html`
         <lia-page title="Sessions" icon="fa-solid fa-plug" description="Everything signed in as you.">
            <div class="d-flex flex-column gap-3">
               ${breadcrumb([{ label: 'Your profile', url: PROFILE_PATH }, { label: 'Sessions' }])}
               ${this.error ? errorPanel(this.error, () => this.reload()) : nothing}
               ${this.notice
                  ? html`<lia-alert variant=${this.notice.variant} message=${this.notice.message} dismissible @lia-dismiss=${() => { this.notice = null; }}></lia-alert>`
                  : nothing}
               ${!this.data && !this.error ? loadingPanel('Loading your sessions') : nothing}
               ${this.data
                  ? html`
                     <div>
                        <lia-data-table
                           listing-id="profile-sessions"
                           .perPage=${10}
                           no-search
                           no-column-manager
                           .columns=${[
                              {
                                 key: 'clientName',
                                 label: 'Client',
                                 sortable: true,
                                 render: row => html`
                                    <i class=${String(row.clientName ?? '').startsWith('Impersonation') ? 'fa-solid fa-user-secret text-warning me-2' : 'fa-solid fa-plug text-body-secondary me-2'} aria-hidden="true"></i>
                                    ${row.clientName || 'A covdbg client'}
                                    ${row.prefix === this.currentPrefix ? html`<span class="badge text-bg-secondary ms-1">This tab</span>` : nothing}`
                              },
                              {
                                 key: 'teamName',
                                 label: 'Team',
                                 sortable: true,
                                 render: row => row.teamName || html`<span class="text-body-secondary">None (web)</span>`
                              },
                              { key: 'createdAt', label: 'Signed in', sortable: true, render: row => ago(row.createdAt) },
                              { key: 'lastUsedAt', label: 'Last activity', sortable: true, render: row => (row.lastUsedAt ? ago(row.lastUsedAt) : 'None yet') },
                              { key: 'expiresAt', label: 'Expires', sortable: true, render: row => ago(row.expiresAt) }
                           ]}
                           .rows=${rows}
                           .rowKey=${row => row.id}
                           empty-title="Nothing signed in"
                           empty-message="No client holds a session as you."
                           .rowActions=${row => (row.prefix === this.currentPrefix
                              ? []
                              : [{
                                 id: 'revoke',
                                 icon: 'fa-solid fa-right-from-bracket',
                                 title: 'Sign this session out',
                                 variant: 'outline-danger',
                                 size: 'sm',
                                 disabled: this.busy,
                                 confirm: {
                                    title: 'Sign this session out?',
                                    message: `${row.clientName || 'This client'} has to sign in again to continue.`,
                                    confirmLabel: 'Sign out',
                                    variant: 'danger'
                                 }
                              }])}
                           @lia-action=${event => { if (event.detail?.row) { void this.revoke(event.detail.row); } }}
                        ></lia-data-table>
                     </div>`
                  : nothing}
            </div>
         </lia-page>`;
   }
}

customElements.define('user-profile-sessions', UserProfileSessions);
